import { Box, IconButton, styled, Typography } from '@mui/material';

import colors from '../../colors';

export const SettingsLayout = styled(Box, { label: "SettingsLayout" })(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
  [theme.breakpoints.down('md')]: {
    flexDirection: "column",
  },
}));

export const SettingsTopBar = styled(Box, { label: "SettingsTopBar" })(({ theme }) => ({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  height: 48,
  padding: "0 16px",
  borderBottom: `1px solid ${colors.black100}`,
  backgroundColor: colors.systemWhite,
  [theme.breakpoints.up('md')]: {
    display: "none",
  },
}));

export const SettingsMenuButton = styled(IconButton, { label: "SettingsMenuButton" })(({ theme }) => ({
  width: 36,
  height: 36,
  color: colors.black700,
  [theme.breakpoints.up('md')]: {
    display: "none",
  },
}));

export const SettingsNav = styled("nav", { label: "SettingsNav" })(({ theme }) => ({
  flex: "0 0 224px",
  width: 224,
  minHeight: 0,
  padding: "20px 12px",
  borderRight: `1px solid ${colors.black100}`,
  backgroundColor: colors.systemWhite,
  overflowY: "auto",
  overflowX: "hidden",
  [theme.breakpoints.down('md')]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    flex: "0 0 auto",
    width: "100%",
    maxHeight: "40vh",
    gap: 8,
    padding: "12px 16px",
    borderRight: 0,
    borderBottom: `1px solid ${colors.black100}`,
    overflowY: "auto",
    overflowX: "hidden",
  },
}));

export const SettingsPrimaryNav = styled("div", { label: "SettingsPrimaryNav" })(({ theme }) => ({
  display: "block",
  [theme.breakpoints.down('md')]: {
    display: "flex",
    alignItems: "flex-start",
    overflowX: "auto",
    overflowY: "hidden",
    gap: 4,
    paddingBottom: 2,
  },
}));

export const NavGroup = styled("div", { label: "SettingsNavGroup" })(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  marginBottom: 2,
  [theme.breakpoints.down('md')]: {
    alignItems: "flex-start",
    alignSelf: "flex-start",
    flexShrink: 0,
    marginBottom: 0,
  },
}));

export const SettingsNavButton = styled("button", {
  shouldForwardProp: prop => prop !== 'active',
  label: "SettingsNavButton",
})<{ active?: boolean }>(({ active, theme }) => ({
  boxSizing: "border-box",
  height: 34,
  width: "auto",
  flex: "0 1 auto",
  maxWidth: "100%",
  padding: "7px 10px",
  border: 0,
  borderRadius: 6,
  backgroundColor: "transparent",
  color: active ? colors.lavendar900 : colors.black600,
  cursor: "pointer",
  font: "inherit",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  textAlign: "left",
  "&:hover": {
    backgroundColor: colors.lavendar50,
    color: active ? colors.lavendar950 : colors.black700,
  },
  "&:disabled": {
    color: colors.black400,
    cursor: "default",
  },
  [theme.breakpoints.down('md')]: {
    whiteSpace: "nowrap",
  },
}));

export const SettingsNavButtonLabel = styled("span", {
  shouldForwardProp: prop => prop !== 'active',
  label: "SettingsNavButtonLabel",
})<{ active?: boolean }>(({ active }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  paddingBottom: 2,
  borderBottom: `2px solid ${active ? colors.lavendar900 : "transparent"}`,
}));

export const SettingsSubNav = styled("div", { label: "SettingsSubNav" })(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  margin: "2px 0 8px 0",
  paddingLeft: 0,
  [theme.breakpoints.down('md')]: {
    display: "none",
  },
}));

export const MobileSettingsSubNav = styled("div", { label: "MobileSettingsSubNav" })(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down('md')]: {
    display: "flex",
    overflowX: "auto",
    overflowY: "hidden",
    gap: 4,
    paddingTop: 2,
    paddingBottom: 2,
  },
  [theme.breakpoints.down('sm')]: {
    flexWrap: "wrap",
    overflowX: "visible",
  },
}));

export const SettingsSubNavButton = styled("button", {
  shouldForwardProp: prop => prop !== 'active',
  label: "SettingsSubNavButton",
})<{ active?: boolean }>(({ active, theme }) => ({
  boxSizing: "border-box",
  height: 34,
  width: "auto",
  maxWidth: "100%",
  padding: "7px 10px",
  border: 0,
  borderRadius: 6,
  backgroundColor: "transparent",
  color: active ? colors.lavendar900 : colors.black600,
  cursor: "pointer",
  font: "inherit",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  textAlign: "left",
  display: "inline-flex",
  alignItems: "center",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: colors.lavendar50,
    color: active ? colors.lavendar950 : colors.black700,
  },
  [theme.breakpoints.down('md')]: {
    flex: "0 1 auto",
  },
  [theme.breakpoints.down('sm')]: {
    flex: "1 1 calc(50% - 4px)",
    width: "auto",
  },
}));

export const SettingsSubNavButtonLabel = styled("span", {
  shouldForwardProp: prop => prop !== 'active',
  label: "SettingsSubNavButtonLabel",
})<{ active?: boolean }>(({ active }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  paddingBottom: 2,
  borderBottom: `2px solid ${active ? colors.lavendar900 : "transparent"}`,
}));

export const SettingsVersionText = styled("div", { label: "SettingsVersionText" })({
  marginTop: 32,
  padding: "0 10px",
  color: colors.black400,
  fontSize: 12,
  lineHeight: "16px",
  whiteSpace: "nowrap",
});

export const NavLabelContent = styled(Box, { label: "SettingsNavLabelContent" })({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

export const SettingsContent = styled(Box, { label: "SettingsContent" })({
  flex: "1 1 0",
  minWidth: 0,
  minHeight: 0,
  overflow: "auto",
});

export const SettingsSectionHeader = styled(Box, { label: "SettingsSectionHeader" })(({ theme }) => ({
  padding: "14px 24px",
  borderBottom: `1px solid ${colors.black100}`,
  backgroundColor: colors.systemWhite,
  position: "sticky",
  top: 0,
  zIndex: 2,
  [theme.breakpoints.down('sm')]: {
    padding: "12px 16px",
  },
}));

export const SettingsBreadcrumb = styled("nav", { label: "SettingsBreadcrumb" })({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 8,
  color: colors.black600,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
});

export const SettingsBreadcrumbButton = styled("button", { label: "SettingsBreadcrumbButton" })({
  border: 0,
  background: "transparent",
  padding: 0,
  color: colors.black600,
  cursor: "pointer",
  font: "inherit",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
  "&:hover": {
    color: colors.lavendar900,
  },
});

export const SettingsBreadcrumbCurrent = styled("span", { label: "SettingsBreadcrumbCurrent" })({
  color: colors.lavendar900,
});

export const SettingsBreadcrumbSeparator = styled("span", { label: "SettingsBreadcrumbSeparator" })({
  color: colors.black400,
});

export const SettingsSectionTabs = styled("div", { label: "SettingsSectionTabs" })(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginTop: 12,
  overflow: "visible",
  paddingBottom: 2,
  [theme.breakpoints.down('sm')]: {
    marginLeft: -4,
    marginRight: -4,
    paddingLeft: 4,
    paddingRight: 4,
  },
}));

export const SettingsHomeList = styled("div", { label: "SettingsHomeList" })({
  display: "flex",
  flexDirection: "column",
  maxWidth: 760,
  borderTop: `1px solid ${colors.black100}`,
});

export const SettingsHomeRow = styled("button", { label: "SettingsHomeRow" })<{ disabled?: boolean }>(
  ({ disabled }) => ({
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 16,
    alignItems: "center",
    padding: "18px 0",
    border: 0,
    borderBottom: `1px solid ${colors.black100}`,
    background: "transparent",
    cursor: disabled ? "default" : "pointer",
    textAlign: "left",
    opacity: disabled ? 0.55 : 1,
    "&:hover": disabled ? {} : {
      color: colors.lavendar900,
    },
  })
);

export const SettingsHomeRowTitle = styled(Typography, { label: "SettingsHomeRowTitle" })({
  color: colors.black900,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "22px",
  marginBottom: 4,
});

export const SettingsHomeRowDescription = styled(Typography, { label: "SettingsHomeRowDescription" })({
  color: colors.black600,
  fontSize: 14,
  lineHeight: "20px",
});

export const SettingsHomeRowArrow = styled("span", { label: "SettingsHomeRowArrow" })({
  color: colors.black500,
  fontSize: 20,
  lineHeight: 1,
});

export const PaddedSettingsContent = styled(Box, { label: "PaddedSettingsContent" })(({ theme }) => ({
  paddingLeft: 24,
  paddingRight: 24,
  paddingTop: 32,
  paddingBottom: 32,
  [theme.breakpoints.down('sm')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 24,
  },
}));

export const SettingsPageTitle = styled(Typography, { label: "SettingsPageTitle" })({
  fontSize: 24,
  fontWeight: 700,
  lineHeight: "32px",
  marginBottom: 16,
  color: colors.black900,
});

export const SettingsPageDescription = styled(Typography, { label: "SettingsPageDescription" })({
  marginBottom: 16,
});

export const LogoutContent = styled(Box, { label: "LogoutContent" })({
  padding: 32,
});

export const LogoutActions = styled(Box, { label: "LogoutActions" })({
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
});
